import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import { UsersIcon, FingerPrintIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

// Side navigation items with conditional inclusion based on role
const getSideNavigation = (userRole) => {
  const items = [
    { name: 'Security', href: '/admin/security', icon: FingerPrintIcon, current: true },
  ];
  
  // Only show Team option for admin users
  if (userRole === 'admin') {
    items.unshift({ name: 'Team', href: '/admin/team', icon: UsersIcon, current: false });
  }
  
  return items;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Security component for users to update their password
 */
const Security = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const result = await changePassword(currentPassword, newPassword);
      
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="rounded-lg bg-white shadow-sm">
      <div className="mx-auto max-w-7xl pt-4 lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">Security Settings</h1>

        {/* Sidebar navigation */}
        <aside className="flex overflow-x-auto border-b border-gray-900/5 py-2 lg:block lg:w-64 lg:flex-none lg:border-0 lg:py-8">
          <nav className="flex-none px-4 sm:px-6 lg:px-0">
            <ul role="list" className="flex gap-x-3 gap-y-1 whitespace-nowrap lg:flex-col">
              {getSideNavigation(user?.role).map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-gray-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                      'group flex gap-x-3 rounded-md py-2 pr-3 pl-2 text-sm/6 font-semibold',
                    )}
                  >
                    <item.icon
                      aria-hidden="true"
                      className={classNames(
                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                        'size-6 shrink-0',
                      )}
                    />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main content */}
        <main className="px-4 py-4 sm:px-6 lg:flex-auto lg:px-0 lg:py-8">
          <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {success && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">{success}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {/* Change Password Section */}
            <div>
              <h2 className="text-base/7 font-semibold text-gray-900">Change Password</h2>
              <p className="mt-1 text-sm/6 text-gray-500">
                Update your password for {user?.email}
              </p>
              
              <form onSubmit={handleSubmit} className="mt-6">
                <div className="space-y-6 divide-y divide-gray-100 border-t border-gray-200">
                  <div className="py-6 sm:flex">
                    <label htmlFor="current-password" className="text-sm font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Current Password
                    </label>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                      <input
                        id="current-password"
                        name="current-password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                  
                  <div className="py-6 sm:flex">
                    <label htmlFor="new-password" className="text-sm font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      New Password
                    </label>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                      <input
                        id="new-password"
                        name="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                  
                  <div className="py-6 sm:flex">
                    <label htmlFor="confirm-password" className="text-sm font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Confirm New Password
                    </label>
                    <div className="mt-1 flex flex-col sm:mt-0 sm:flex-auto">
                      <input
                        id="confirm-password"
                        name="confirm-password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="block w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-base text-gray-900 shadow-sm outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex pt-6 border-t border-gray-100">
                  <button
                    type="submit"
                    className="ml-auto rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-300"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Security;